import React, { useCallback } from 'react';

import { useIsFocused, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { LoadingAnimation } from '../../../../components';
import { useGroupOrderingContent, useHomeContent, useHomeLastOrderStatus } from '../../hooks';
import { HomeScreenContentSM } from './HomeScreenContentSM';
import { HomeScreenContentXS } from './HomeScreenContentXS';

export const HomeScreenContent = () => {
  const { currentBreakpoint } = useResponsive();

  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');
  const isScreenFocused = useIsFocused();

  // ─── Telemetry ───────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const handleCtaPress = useCallback(() => {
    track('home.hero.cta_tapped');
  }, [track]);

  const handleSecondaryCtaPress = useCallback(() => {
    track('group_ordering.entry_point.tapped');
  }, [track]);

  const handleBagPress = useCallback(() => {
    track('home.bag.cta_tapped');
  }, [track]);

  // ─── Data ────────────────────────────────────────────────────────────
  // Used from 'cache-only' in internal components.

  useHomeLastOrderStatus('cache-and-network');

  // ─── Contentful ──────────────────────────────────────────────────────

  const { hasPlacedOrder, homeContent, isFetchingHomeContent } = useHomeContent();
  const { mappedContent: groupOrderingContent, isFetching: isFetchingGroupOrderingContent } =
    useGroupOrderingContent({ pause: !isScreenFocused });

  // ─── Rendering ───────────────────────────────────────────────────────

  if (isFetchingHomeContent || isFetchingGroupOrderingContent) {
    return <LoadingAnimation />;
  }

  if (currentBreakpoint.isXS) {
    return (
      <HomeScreenContentXS
        homeContent={homeContent}
        bagPressed={handleBagPress}
        ctaPressed={handleCtaPress}
        secondaryCtaLabel={groupOrderingContent.entryPointCtaLabel}
        secondaryCtaPressed={handleSecondaryCtaPress}
        shouldRenderSecondaryCta={isGroupOrderingEnabled}
      />
    );
  }

  return (
    <HomeScreenContentSM
      homeContent={homeContent}
      hasPlacedOrder={hasPlacedOrder}
      ctaPressed={handleCtaPress}
      secondaryCtaLabel={groupOrderingContent.entryPointCtaLabel}
      secondaryCtaPressed={handleSecondaryCtaPress}
      shouldRenderSecondaryCta={isGroupOrderingEnabled}
    />
  );
};
